import { useEffect, useState } from "react";
const setKeyState = (key, localState, storage) => {
    // Attach a timestamp, so we can keep track if local storage is too stale
    const stateWithTime = Object.assign({ localState }, { timeStamp: Date.now() });
    // Set local storage
    storage.setItem(key, JSON.stringify(stateWithTime));
};
/**
 * A hook to handle storing data in localStorage, if there is no localStorage data we show default value.
 * @param key - unique storage key
 * @param defaultValue - default value for the variable
 * @param storage - Storage object to use (localStorage, sessionStorage or custom Storage object)
 * @param expiryTime - expiry time
 */
export const useStorageState = (key, defaultValue, storage, expiryTime) => {
    // Usage of state manager to get and store states.
    const [localState, setLocalState] = useState(() => {
        // Retrieve local storage data if it exists
        const persistedState = storage.getItem(key);
        if (!persistedState) {
            return defaultValue;
        }
        // Extract time stamp
        const { timeStamp } = JSON.parse(persistedState);
        // Get current time
        const currentTime = Date.now();
        // Check if time has exceeded the local storage expiry time
        if (expiryTime && currentTime - timeStamp > expiryTime) {
            // Clear cache
            storage.removeItem(key);
            // Show default value
            return defaultValue;
        }
        const parsedLocalState = JSON.parse(persistedState).localState;
        // update stored value to refresh timestamp
        setKeyState(key, parsedLocalState, storage);
        return parsedLocalState;
    });
    useEffect(() => {
        setKeyState(key, localState, storage);
    }, [localState, key, storage]);
    return [localState, setLocalState];
};
export const useLocalStorageState = (key, defaultValue, expiryTime) => useStorageState(key, defaultValue, localStorage, expiryTime);
export const useSessionStorageState = (key, defaultValue, expiryTime) => useStorageState(key, defaultValue, sessionStorage, expiryTime);
//# sourceMappingURL=useStorageState.js.map